import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { api } from "../lib/api";
import { useResource } from "../lib/hooks";

/**
 * Full onboarding-pack editor: name/description plus which admin fields and
 * profile items the pack requires, its required document types, and its policy
 * set. Editing a pack affects only future assignments — the snapshot each person
 * carries (employee_onboarding / user_policy_assignment) is untouched.
 */

const ADMIN_FIELDS: Array<[string, string]> = [
    ["start_date", "Start date"], ["team_id", "Team"], ["department_id", "Department"],
    ["branch_id", "Branch"], ["responsibility_id", "Responsibility"], ["pay_type_id", "Pay type"],
    ["overtime_eligibility_id", "Overtime eligibility"], ["primary_job_role_id", "Role"],
    ["direct_manager_id", "Direct manager"], ["employment_type", "Employment type"], ["standard_hours", "Standard hours"],
];
const PROFILE_FIELDS: Array<[string, string]> = [
    ["title", "Title"], ["date_of_birth", "Birthday"], ["gender", "Gender"], ["address", "Home address"],
    ["emergency_contact", "Emergency contact name"], ["emergency_contact_relationship", "Relationship"], ["emergency_phone", "Emergency phone"],
];

interface Pack {
    id: string; name: string; description?: string | null;
    required_admin_fields?: string[]; required_profile_fields?: string[];
}
interface DocType { id: string; name: string }
interface Policy { id: string; name: string; policy_group_id: string; status: string }

const Chips = ({ options, selected, onToggle }: {
    options: Array<[string, string]>; selected: Set<string>; onToggle: (v: string) => void;
}) => (
    <div className="chip-picker">
        {options.map(([value, label]) => (
            <button type="button" key={value} className={`chip-toggle ${selected.has(value) ? "on" : ""}`}
                aria-pressed={selected.has(value)} onClick={() => onToggle(value)}>{label}</button>
        ))}
    </div>
);

export function PackEditor({ pack, onClose, onSaved }: { pack: Pack; onClose: () => void; onSaved: () => void }) {
    const docTypes = useResource<DocType[]>("/document-types");
    const policies = useResource<Policy[]>("/policies");
    const packDocs = useResource<Array<{ id: string; document_type_id: string }>>(`/onboarding-pack-documents?pack_id=${pack.id}`);
    const packItems = useResource<Array<{ id: string; policy_group_id: string }>>(`/policy-pack-items?pack_id=${pack.id}`);

    const [name, setName] = useState(pack.name);
    const [description, setDescription] = useState(pack.description ?? "");
    const [admin, setAdmin] = useState<Set<string>>(new Set(pack.required_admin_fields ?? []));
    const [profile, setProfile] = useState<Set<string>>(new Set(pack.required_profile_fields ?? []));
    const [docs, setDocs] = useState<Set<string>>(new Set());
    const [pols, setPols] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => { if (packDocs.data) setDocs(new Set(packDocs.data.map((d) => d.document_type_id))); }, [packDocs.data]);
    useEffect(() => { if (packItems.data) setPols(new Set(packItems.data.map((i) => i.policy_group_id))); }, [packItems.data]);

    const toggle = (set: Set<string>, setter: (s: Set<string>) => void, v: string) => {
        const next = new Set(set);
        if (next.has(v)) next.delete(v);
        else next.add(v);
        setter(next);
    };

    const save = async () => {
        setBusy(true); setError(null);
        try {
            await api.put(`/policy-packs/${pack.id}`, {
                name, description: description || null,
                required_admin_fields: [...admin], required_profile_fields: [...profile],
            });
            // Reconcile document types.
            const curDocs = new Map((packDocs.data ?? []).map((d) => [d.document_type_id, d.id]));
            for (const id of docs) if (!curDocs.has(id)) await api.post("/onboarding-pack-documents", { pack_id: pack.id, document_type_id: id, is_required: true });
            for (const [typeId, rowId] of curDocs) if (!docs.has(typeId)) await api.delete(`/onboarding-pack-documents/${rowId}`);
            // Reconcile policies.
            const curPols = new Map((packItems.data ?? []).map((i) => [i.policy_group_id, i.id]));
            for (const gid of pols) if (!curPols.has(gid)) await api.post("/policy-pack-items", { pack_id: pack.id, policy_group_id: gid, is_required: true });
            for (const [gid, rowId] of curPols) if (!pols.has(gid)) await api.delete(`/policy-pack-items/${rowId}`);
            onSaved();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not save the pack");
        } finally { setBusy(false); }
    };

    const currentPolicies = (policies.data ?? []).filter((p) => p.status === "current");

    return (
        <Modal title={`Edit pack — ${pack.name}`} width="wide" onClose={onClose}
            footer={<>
                <button className="btn ghost" onClick={onClose}>Cancel</button>
                <button className="btn" disabled={busy} onClick={save}>Save pack</button>
            </>}>
            {error && <div className="error-banner">{error}</div>}
            <div className="pack-editor-group">
                <label>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="pack-editor-group">
                <label>Description</label>
                <input value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="pack-editor-group">
                <label>Required employee-information fields (admin)</label>
                <Chips options={ADMIN_FIELDS} selected={admin} onToggle={(v) => toggle(admin, setAdmin, v)} />
            </div>
            <div className="pack-editor-group">
                <label>Required profile fields (user)</label>
                <Chips options={PROFILE_FIELDS} selected={profile} onToggle={(v) => toggle(profile, setProfile, v)} />
            </div>
            <div className="pack-editor-group">
                <label>Required documents</label>
                <Chips options={(docTypes.data ?? []).map((d) => [d.id, d.name])} selected={docs} onToggle={(v) => toggle(docs, setDocs, v)} />
            </div>
            <div className="pack-editor-group">
                <label>Policies to acknowledge</label>
                <Chips options={currentPolicies.map((p) => [p.policy_group_id, `${p.name} v${(p as unknown as { version?: string }).version ?? ""}`])}
                    selected={pols} onToggle={(v) => toggle(pols, setPols, v)} />
            </div>
        </Modal>
    );
}
